import { type JSX, useCallback, useEffect, useMemo, useState } from 'react'
import { Center, IconImageSquare } from '@vtex/admin-ui'
import { transformImageURL } from '@/lib/images'

import * as styles from './ImageWithFallback.styles'

export type ImageWithFallbackProps = JSX.IntrinsicElements['img'] & {
  withBorder?: boolean
}

export const ImageWithFallback = ({
  src,
  className = '',
  width = 48,
  height = 48,
  alt = '',
  withBorder = true,
  onError: onErrorProp,
  onLoad: onLoadProp,
  onLoadStart: onLoadStartProp,
  ...restOfProps
}: ImageWithFallbackProps) => {
  const [showFallback, setShowFallback] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const style = `${styles.wrapper(width, height, withBorder)} ${className}`
  const transformedSrc = useMemo(
    () =>
      typeof src === 'string' ? transformImageURL(src, Number(width)) : src,
    [src, width]
  )

  useEffect(() => {
    if (transformedSrc) {
      setIsLoading(true)
      setShowFallback(false)
    }
  }, [transformedSrc])

  const onError = useCallback(
    (evt: React.SyntheticEvent<HTMLImageElement>) => {
      setShowFallback(true)
      setIsLoading(false)
      onErrorProp?.(evt)
    },
    [onErrorProp]
  )

  const onLoad = useCallback(
    (evt: React.SyntheticEvent<HTMLImageElement>) => {
      setIsLoading(false)
      onLoadProp?.(evt)
    },
    [onLoadProp]
  )

  const onLoadStart = useCallback(
    (evt: React.SyntheticEvent<HTMLImageElement>) => {
      setIsLoading(true)
      onLoadStartProp?.(evt)
    },
    [onLoadStartProp]
  )

  if (showFallback || !transformedSrc) {
    return (
      <Center className={style}>
        <IconImageSquare
          color="gray"
          width={width}
          height={height}
          weight="fill"
          aria-hidden
          opacity={0.7}
          transform="scale(0.5)"
        />
      </Center>
    )
  }

  return (
    <picture className={style}>
      {isLoading && (
        <div className={styles.placeholder(width, height)} aria-hidden="true" />
      )}
      <img
        {...restOfProps}
        alt={alt}
        src={transformedSrc}
        className={styles.image}
        onLoad={onLoad}
        onLoadStart={onLoadStart}
        onError={onError}
        loading="lazy"
        crossOrigin="anonymous"
        referrerPolicy="no-referrer"
      />
    </picture>
  )
}
